const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getToken = () => localStorage.getItem('tennisbuzz_token');

const headers = (json = true) => {
  const h = {};
  if (json) h['Content-Type'] = 'application/json';
  const token = getToken();
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

const normalize = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(normalize);
  
  const out = { ...obj };
  
  // Convert _id to id
  if (out._id) out.id = String(out._id);
  
  // Normalize all nested properties
  Object.keys(out).forEach(k => {
    if (Array.isArray(out[k])) {
      out[k] = out[k].map(item => {
        // Handle both objects and primitive values
        if (item && typeof item === 'object') {
          return normalize(item);
        }
        return item;
      });
    } else if (out[k] && typeof out[k] === 'object' && !Array.isArray(out[k])) {
      out[k] = normalize(out[k]);
    }
  });
  
  return out;
};

const request = async (url, options = {}) => {
  try {
    const res = await fetch(`${BASE}${url}`, { headers: headers(), ...options });
    const data = await res.json();

    if (res.status === 401) {
      localStorage.removeItem('tennisbuzz_token');
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      const error = new Error(data.message || 'Session expired');
      error.response = { data };
      throw error;
    }

    if (!res.ok) {
      const error = new Error(data.message || `Request failed with status ${res.status}`);
      error.response = { data };
      throw error;
    }

    return { ...data, data: normalize(data.data) };
  } catch (err) {
    throw err;
  }
};

export const groupStageAPI = {
  // Create group stage
  createGroupStage: (tournamentId, config) =>
    request(`/group-stage/${tournamentId}/create`, {
      method: 'POST',
      body: JSON.stringify(config),
    }),

  // Get group stage details
  getGroupStage: (groupStageId) =>
    request(`/group-stage/${groupStageId}/details`),

  // Assign participants to groups
  assignGroups: (groupStageId, groupAssignments) =>
    request(`/group-stage/${groupStageId}/assign-groups`, {
      method: 'POST',
      body: JSON.stringify({ groupStageId, groupAssignments }),
    }),

  // Generate fixtures
  generateFixtures: (groupStageId) =>
    request(`/group-stage/${groupStageId}/generate-fixtures`, {
      method: 'POST',
    }),

  // Get group fixtures
  getGroupFixtures: (groupId) =>
    request(`/group-stage/group/${groupId}/fixtures`),

  // Record match result
  recordResult: (matchId, result) =>
    request(`/group-stage/match/${matchId}/result`, {
      method: 'POST',
      body: JSON.stringify(result),
    }),

  // Get standings
  getStandings: (groupId) =>
    request(`/group-stage/group/${groupId}/standings`),

  // Publish standings
  publishStandings: (groupId) =>
    request(`/group-stage/group/${groupId}/publish-standings`, {
      method: 'POST',
    }),

  // Get audit history
  getAuditHistory: (groupStageId, limit = 100) =>
    request(`/group-stage/${groupStageId}/audit?limit=${limit}`),

  // Get group stage summary for tournament
  getSummary: (tournamentId) =>
    request(`/group-stage/tournament/${tournamentId}/summary`),

  // Get tournament details (for organizer view)
  getTournament: (tournamentId) =>
    request(`/tournaments/${tournamentId}`),

  // Get group stage for a tournament
  getGroupStageForTournament: (tournamentId) =>
    request(`/group-stage/tournament/${tournamentId}/details`),

  // Get group standings
  getGroupStandings: (groupId) =>
    request(`/group-stage/group/${groupId}/standings`),

  // Get team details
  getTeamDetails: (teamId) =>
    request(`/group-team/team/${teamId}/details`),

  // Get group teams
  getGroupTeams: (groupId) =>
    request(`/group-team/group/${groupId}/teams`),

  // CREATE GROUP
  createGroup: (groupStageId, groupData) =>
    request(`/group-stage/${groupStageId}/create-group`, {
      method: 'POST',
      body: JSON.stringify(groupData),
    }),

  // DELETE GROUP
  deleteGroup: (groupId) =>
    request(`/group-stage/group/${groupId}`, {
      method: 'DELETE',
    }),

  // CREATE TEAM
  createTeam: (groupId, teamData) =>
    request(`/group-team/group/${groupId}/create-team`, {
      method: 'POST',
      body: JSON.stringify(teamData),
    }),

  // UPDATE TEAM
  updateTeam: (teamId, teamData) =>
    request(`/group-team/team/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    }),

  // DELETE TEAM
  deleteTeam: (teamId) =>
    request(`/group-team/team/${teamId}`, {
      method: 'DELETE',
    }),
};
